import { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GenericOption,
  MatchingRule,
  SmartConnectorVariable,
} from '__pages/SmartConnectors/types';
import { useMatchingRulesErrors } from './useMatchingRuleErrors';
import { PrimaryObjectUploadContext } from '../../context';
import {
  StyledBoldSelect,
  StyledDeleteButton,
  StyledDiv,
  StyledRow,
  StyledRuleCard,
  StyledRuleWarpper,
  StyledSubheader,
} from './styles';
import Select from '__components/Inputs/Select';
import { ReorderButtons } from './ReorderButtons';
import { getFieldTypeMeta } from '../MappingStep/helpers';
import { getVariablesGroupedOptions } from 'pages/SmartConnectors/SmartConnector/helpers';
import {
  SMART_FIELD_TYPES,
  KIZEN_ID_OPTION,
} from 'pages/SmartConnectors/constants';

export type OnChangeMatchingRuleHandler = ({
  id,
  prop,
  value,
}: {
  id: string;
  prop: keyof MatchingRule;
  value: MatchingRule[keyof MatchingRule];
}) => void;

type MatchingRuleSettingsProps = {
  rule: MatchingRule;
  index: number;
  isLastIndex: boolean;
  onChange: OnChangeMatchingRuleHandler;
  onDelete: (id: string, index: number) => void;
  onReorder: (index: number, offset: 1 | -1) => void;
  fieldOptions: { label: string; options: GenericOption[] }[];
  variables: SmartConnectorVariable[];
  isLastExecutableRule: boolean;
};

type ActionsConfig = {
  action: keyof Pick<
    MatchingRule,
    | 'match_archive_action'
    | 'no_match_action'
    | 'single_match_action'
    | 'multiple_match_action'
  >;
  label: string;
}[];

export const MatchingRuleSettings = ({
  rule,
  index,
  isLastIndex,
  onChange,
  onDelete,
  onReorder,
  fieldOptions,
  variables,
  isLastExecutableRule,
}: MatchingRuleSettingsProps) => {
  const { t } = useTranslation();
  const { metaData, customObject, variablesById } = useContext(
    PrimaryObjectUploadContext
  );

  const { id, field, variable, is_match_by_kizen_id } = rule;

  const fieldValue = useMemo(() => {
    if (is_match_by_kizen_id) {
      return KIZEN_ID_OPTION;
    }
    const selectedField = customObject?.fields?.find(({ id }) => id === field);
    return (
      selectedField && {
        value: selectedField.id,
        label: selectedField.displayName,
      }
    );
  }, [field, is_match_by_kizen_id, customObject?.fields]);

  const { hasError, ruleError, errorKey, validate, clearError } =
    useMatchingRulesErrors(index);

  const onDeleteRule = () => {
    onDelete(id!, index);
  };

  const onChangeField = ({ value }: { value: string }) => {
    if (value === SMART_FIELD_TYPES.kizen_id) {
      onChange({ id, prop: 'field', value: null });
    } else {
      onChange({ id, prop: 'field', value });
    }
    clearError('field');
  };

  const onChangeVariable = ({ value }: { value: string }) => {
    onChange({ id, prop: 'variable', value });
    clearError('variable');
  };

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errorKey) {
      wrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [errorKey]);

  const actions = useMemo<ActionsConfig>(
    () => [
      { action: 'no_match_action', label: t('If No Match Found') },
      {
        action: 'single_match_action',
        label: t('If Single Match Found'),
      },
      {
        action: 'multiple_match_action',
        label: t('If Multiple Matches Found'),
      },
      {
        action: 'match_archive_action',
        label: t('If Archived Match Found'),
      },
    ],
    [t]
  );

  const variableOptions = useMemo(() => {
    const ruleFieldType = is_match_by_kizen_id
      ? SMART_FIELD_TYPES.kizen_id
      : (customObject?.fields || []).find(({ id }) => id === field)?.fieldType;

    const filteredVariables = ruleFieldType
      ? variables.filter((variable) =>
          getFieldTypeMeta(metaData, variable, ruleFieldType)
        )
      : [];
    return getVariablesGroupedOptions(filteredVariables, t);
  }, [
    variables,
    metaData,
    field,
    customObject?.fields,
    is_match_by_kizen_id,
    t,
  ]);

  return (
    <StyledRuleWarpper
      ref={wrapperRef}
      hasError={hasError}
      isLastExecutableRule={isLastExecutableRule}
      data-qa-rule-id={rule.id}
      data-qa-rule-index={index}
    >
      <ReorderButtons
        index={index}
        isFirst={index === 0}
        isLast={isLastIndex}
        onReorder={onReorder}
      />
      <StyledRuleCard>
        <StyledRow>
          <StyledDiv width={303}>
            <StyledBoldSelect
              value={fieldValue}
              options={fieldOptions}
              label={t('Kizen Field for Matching')}
              placeholder={t('Choose Kizen Field')}
              onChange={onChangeField}
              error={ruleError?.field}
              validate={errorKey === 'field' ? validate : undefined}
            />
          </StyledDiv>
          {field || is_match_by_kizen_id ? (
            <StyledDiv width={257}>
              <StyledBoldSelect
                value={
                  variable &&
                  variablesById[variable] && {
                    value: variablesById[variable]?.id,
                    label: variablesById[variable]?.name,
                  }
                }
                options={variableOptions}
                label={t('Variable to Match')}
                placeholder={t('Choose Variable')}
                onChange={onChangeVariable}
                error={ruleError?.variable}
                validate={errorKey === 'variable' ? validate : undefined}
              />
            </StyledDiv>
          ) : null}
          <StyledDeleteButton color="red" variant="text" onClick={onDeleteRule}>
            {t('Delete Field Match')}
          </StyledDeleteButton>
        </StyledRow>
        <StyledSubheader weight="bold">{t('Upload Functions')}</StyledSubheader>
        {actions.map(({ action, label }) => {
          const onChangeAction = ({ value }: { value: string }) => {
            onChange({ id, prop: action, value });
            clearError(action);
          };
          return (
            <StyledDiv width={303} key={action}>
              <Select
                value={rule[action]}
                options={metaData.matching_rules[`${action}s`]}
                label={label}
                onChange={onChangeAction}
                error={ruleError?.[action]}
                validate={errorKey === action ? validate : undefined}
              />
            </StyledDiv>
          );
        })}
      </StyledRuleCard>
    </StyledRuleWarpper>
  );
};
